import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import secureLocalStorage from "react-secure-storage";

const ResearchPublication = () => {
  const [form, setForm] = useState({});
  const [publicationList, setPublicationList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const publicationPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				user: secureLocalStorage.getItem("user"),
				token:secureLocalStorage.getItem("token"),
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/publication-list", payload);
			
			// Loop data (array)
			setPublicationList(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		publicationPost();		
	}, []);
  

	// ✅ Input handlers
	const handleChange = (e) => {
		const { name, value } = e.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	};
	const handleSave = async () => {
	  try {
		// Create a FormData payload
		const payload = new FormData();

		// Add secure credentials
		payload.append("userId", import.meta.env.VITE_API_USER);
		payload.append("password", import.meta.env.VITE_API_PASS);
		payload.append("user", secureLocalStorage.getItem("user"));
		payload.append("token", secureLocalStorage.getItem("token"));
		// Append education form fields
		Object.entries(form).forEach(([key, value]) => {
		  if (value !== undefined && value !== null) {
			payload.append(key, value);
		  }
		});

		let res;

		// --- Update existing education record ---
		if (editingIndex !== null) {
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/update-publication", payload);		 

			// Update local state list
			const updated = [...publicationList];
			updated[editingIndex] = res.data;
			setPublicationList(res.data);
			setEditingIndex(null);
		}else {
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/update-publication", payload);

			setPublicationList(res.data);
		}

		// --- Reset form ---
		setForm({});
		
		// --- Handle response feedback ---
		const code = res?.data?.responseCode?.toString();
		if (code === "200") {
		  alert(res?.data?.responseMsg || "✅ Publication info saved successfully!");
		} else {
		  alert(res?.data?.responseMsg || "✅ Publication info updated successfully!");
		}
	  } catch (err) {		
		alert("❌ Failed to save publication info. Please try again.");
	  }
	};

  
  const handleEdit = (index) => {
    setEditingIndex(index);
    setForm(publicationList[index]);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setForm({});
  };

  if (loading)
    return <div className="text-center text-blue-600 py-8">Loading Publications...</div>;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 shadow-md space-y-8"
    >
      <h2 className="text-2xl font-semibold text-[#1A2B63] mb-4">
        Research & Publication
      </h2>

      {/* ==================== FORM ==================== */}
      <form className="grid grid-cols-2 md:grid-cols-3 gap-5">
        {[
          { label: "Publication Title", name: "title" },
          { label: "Year", name: "year", type: "number" },
          { label: "Publisher / Institute / Organization", name: "publisher" },
          { label: "Authors", name: "authors", type: "textarea" },
          { label: "Publication Link/DOI", name: "link", type: "url" },
          { label: "Issue / Volume / Citation / Other Remarks", name: "remarks", type: "textarea" },
        ].map((field, i) => (
          <div
            key={i}
            className={`flex flex-col ${
              field.type === "textarea" || field.name === "remarks"
                ? "col-span-full"
                : ""
            }`}
          >
            <label className="text-gray-700 text-sm font-semibold mb-1">
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <textarea
                name={field.name}
                value={form[field.name] || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 min-h-[80px]"
              />
            ) : (
              <input
                type={field.type || "text"}
                name={field.name}
                value={form[field.name] || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            )}
          </div>
        ))}

        <div className="col-span-full flex justify-end gap-3 mt-2">
          {editingIndex !== null && (
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="bg-blue-600 text-white"
          >
            {editingIndex !== null ? "Update Publication" : "Add Publication"}
          </Button>
        </div>
      </form>

      {/* ==================== LIST ==================== */}
      <div className="mt-8 space-y-4">
        <h3 className="text-xl font-semibold text-[#1A2B63]">
          Publication History
        </h3>

        {Array.isArray(publicationList) && publicationList.length > 0 ? (
          publicationList.map((pub, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-blue-50 rounded-lg flex justify-between items-start shadow-sm hover:shadow-md transition-all"
            >
              <div className="space-y-1 w-full">
                <p className="font-semibold text-blue-800">{pub.title}</p>
                <p className="text-sm text-gray-700">
                  <strong>Authors:</strong> {pub.authors}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Publisher:</strong> {pub.publisher} | <strong>Year:</strong>{" "}
                  {pub.year}
                </p>
                {pub.link && (
                  <a
                    href={pub.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    View Publication
                  </a>
                )}
                {pub.remarks && (
                  <p className="text-sm text-gray-500 italic">
                    {pub.remarks}
                  </p>
                )}
              </div>

              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEdit(i)}
              >
                Edit
              </Button>
            </motion.div>
          ))
        ) : (
          <p className="text-gray-500 italic text-center">
            No publications added yet.
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default ResearchPublication;
